// src/pages/Deposit.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { deposit, getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  ArrowDownRight,
  CheckCircle,
  AlertCircle,
  Wallet as WalletIcon,
  RefreshCw,
  History,
  TrendingUp,
  Calculator,
  Sparkles,
  Shield,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat, formatCrypto } from "../utils/currency";
import { transactionService } from "../services/api";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface CalculatedAmount {
  crypto_amount: number;
  currency: string;
  crypto_price: number;
  usd_amount: number;
  network: string;
  meets_minimum: boolean;
  minimum_required: number;
}

const Deposit: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions,
  );
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    crypto_amount: "",
    currency: "BTC",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });
  const [calculatedAmount, setCalculatedAmount] =
    useState<CalculatedAmount | null>(null);

  useEffect(() => {
    fetchCryptoPrices();
    dispatch(getTransactions({ type: "deposit" }) as any);

    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString(),
      );
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000),
          );
          toast.success("Prices updated successfully");
        }
      } else {
        toast.success("Using cached prices");
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp",
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp",
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const currentPrice = cryptoPrices[formData.currency]?.price;
    if (!currentPrice || currentPrice === 0) {
      toast.error("Please wait for prices to load");
      return;
    }

    const cryptoAmount = parseFloat(formData.crypto_amount);
    const usdAmount = cryptoAmount * currentPrice;
    const minDepositUsd = 10;
    const meetsMinimum = usdAmount >= minDepositUsd;

    const network =
      cryptoPrices[formData.currency]?.network ||
      INITIAL_CURRENCIES[formData.currency as keyof typeof INITIAL_CURRENCIES]
        ?.network ||
      "Unknown Network";

    const localCalculation: CalculatedAmount = {
      crypto_amount: cryptoAmount,
      currency: formData.currency,
      crypto_price: currentPrice,
      usd_amount: usdAmount,
      network: network,
      meets_minimum: meetsMinimum,
      minimum_required: minDepositUsd,
    };

    if (!meetsMinimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(minDepositUsd)}. Your amount is ${formatFiat(
          usdAmount,
        )}.`,
      );
      setCalculatedAmount(localCalculation);
      return;
    }

    setCalculating(true);
    try {
      const response = await transactionService.calculateDepositAmount({
        crypto_amount: cryptoAmount,
        currency: formData.currency,
        current_price: currentPrice,
      });

      if (response.success) {
        setCalculatedAmount(response);
      } else {
        setCalculatedAmount(localCalculation);
        if (response.error) {
          toast.error(response.error);
        }
      }
    } catch (error: any) {
      setCalculatedAmount(localCalculation);
      console.error("Backend calculation failed, using local:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!calculatedAmount) {
      toast.error("Please calculate the amount first");
      return;
    }

    if (!calculatedAmount.meets_minimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(calculatedAmount.minimum_required)}`,
      );
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        deposit({
          crypto_amount: parseFloat(formData.crypto_amount),
          currency: formData.currency,
          current_price: calculatedAmount.crypto_price,
        }) as any,
      );

      toast.success("Deposit submitted successfully!");
      setFormData({ crypto_amount: "", currency: "BTC" });
      setCalculatedAmount(null);
      dispatch(getTransactions({ type: "deposit" }) as any);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit deposit");
    } finally {
      setLoading(false);
    }
  };

  const recentDeposits = transactions
    .filter((t) => t.type === "deposit")
    .slice(0, 3);

  const getCurrentPrice = (currency: string) => {
    return cryptoPrices[currency]?.price || 0;
  };

  const formatPriceDisplay = (price: number, currency: string) => {
    if (price === 0) {
      return fetchingPrices ? "Loading..." : "—";
    }

    if (price > 1000) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (price > 1) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;
    } else {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })}`;
    }
  };

  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }
    return "Never updated";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <ArrowDownRight className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Make Deposit
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Deposit cryptocurrency using live market prices
                </p>
              </div>
            </div>
            <button
              onClick={fetchCryptoPrices}
              disabled={fetchingPrices}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} disabled:opacity-50 group/refresh`}
            >
              {fetchingPrices ? (
                <div className="flex items-center">
                  <div
                    className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                  ></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180" />
                  Refresh Prices
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Deposit Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Price Ticker */}
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Crypto Prices
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp
                  className={`h-4 w-4 ${fetchingPrices ? "text-amber-500" : "text-emerald-500"}`}
                />
                <span
                  className={`${fetchingPrices ? "text-amber-500" : "text-gray-500 dark:text-gray-400"}`}
                >
                  {getLastUpdateDisplay()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.keys(INITIAL_CURRENCIES).map((currency) => {
                const priceData = cryptoPrices[currency];
                const price = priceData?.price || 0;
                const network =
                  priceData?.network ||
                  INITIAL_CURRENCIES[
                    currency as keyof typeof INITIAL_CURRENCIES
                  ]?.network ||
                  "Unknown";

                return (
                  <div
                    key={currency}
                    className={`p-2 rounded-lg text-center transition-all duration-200 group/ticker ${
                      fetchingPrices
                        ? "bg-gray-100 dark:bg-gray-800 animate-pulse"
                        : price === 0
                          ? "bg-gray-100 dark:bg-gray-800"
                          : "bg-gray-50 dark:bg-gray-800/50 hover:bg-primary/5"
                    }`}
                  >
                    <div className="font-semibold text-gray-900 dark:text-white mb-1 group-hover/ticker:text-primary transition-colors">
                      {currency}
                    </div>
                    <div
                      className={`text-xs font-medium ${
                        fetchingPrices
                          ? "text-gray-400 dark:text-gray-500"
                          : price === 0
                            ? "text-gray-500 dark:text-gray-400"
                            : "text-emerald-500"
                      }`}
                    >
                      {formatPriceDisplay(price, currency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {network}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deposit Form */}
          <div className={`${GlobalStyles.card}`}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Deposit Details
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Crypto Amount */}
              <div className="group">
                <label
                  className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                >
                  <span>Cryptocurrency Amount</span>
                  <Sparkles className="h-3 w-3 text-primary" />
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.00000001"
                    value={formData.crypto_amount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        crypto_amount: e.target.value,
                      })
                    }
                    className={`${GlobalStyles.input} pl-10`}
                    placeholder="0.00000000"
                    required
                    min="0.00000001"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <WalletIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <select
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className="bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300"
                      disabled={fetchingPrices}
                    >
                      {Object.keys(INITIAL_CURRENCIES).map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-between mt-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Current price:{" "}
                    {formatPriceDisplay(
                      getCurrentPrice(formData.currency),
                      formData.currency,
                    )}
                  </p>
                  {getCurrentPrice(formData.currency) > 0 &&
                    formData.crypto_amount && (
                      <p className="text-sm text-emerald-500">
                        ≈{" "}
                        {formatFiat(
                          parseFloat(formData.crypto_amount) *
                            getCurrentPrice(formData.currency),
                        )}
                      </p>
                    )}
                </div>
              </div>

              {/* Calculate Button */}
              <div>
                <button
                  type="button"
                  onClick={handleCalculate}
                  disabled={
                    calculating ||
                    !formData.crypto_amount ||
                    fetchingPrices ||
                    getCurrentPrice(formData.currency) === 0
                  }
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group/calc disabled:opacity-50`}
                >
                  {calculating ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Calculating...
                    </div>
                  ) : fetchingPrices ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Loading Prices...
                    </div>
                  ) : getCurrentPrice(formData.currency) === 0 ? (
                    <div className="flex items-center justify-center">
                      <AlertCircle className="h-4 w-4 mr-2 text-amber-500" />
                      No Price Available
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Calculator className="h-4 w-4 mr-2 transition-transform group-hover/calc:scale-110" />
                      Calculate Amount
                    </div>
                  )}
                </button>
              </div>

              {/* Calculated Amount Display */}
              {calculatedAmount && (
                <div
                  className={`p-4 border rounded-xl backdrop-blur-sm ${
                    calculatedAmount.meets_minimum
                      ? "bg-emerald-500/10 border-emerald-200 dark:border-emerald-800"
                      : "bg-amber-500/10 border-amber-200 dark:border-amber-800"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4
                      className={`font-medium ${calculatedAmount.meets_minimum ? "text-emerald-800 dark:text-emerald-300" : "text-amber-800 dark:text-amber-300"}`}
                    >
                      Deposit Summary
                    </h4>
                    {calculatedAmount.meets_minimum ? (
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Cryptocurrency:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCrypto(
                          calculatedAmount.crypto_amount,
                          calculatedAmount.currency,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Exchange Rate:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        1 {calculatedAmount.currency} = $
                        {calculatedAmount.crypto_price.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Network:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {calculatedAmount.network}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-current">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Total Deposit:
                        </span>
                        <span
                          className={`text-lg font-bold ${calculatedAmount.meets_minimum ? "text-emerald-500" : "text-amber-500"}`}
                        >
                          {formatFiat(calculatedAmount.usd_amount)}
                        </span>
                      </div>
                    </div>
                    {!calculatedAmount.meets_minimum && (
                      <div className="mt-2 p-2 bg-rose-500/10 border border-rose-200 dark:border-rose-800 rounded">
                        <p className="text-sm text-rose-600 dark:text-rose-400 text-center">
                          Minimum deposit:{" "}
                          {formatFiat(calculatedAmount.minimum_required)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !calculatedAmount ||
                    fetchingPrices ||
                    !calculatedAmount?.meets_minimum
                  }
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full group/submit disabled:opacity-50`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Processing...
                    </div>
                  ) : fetchingPrices ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Loading...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <ArrowDownRight className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                      <span>Confirm Deposit</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Recent Deposits */}
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recent Deposits
              </h3>
              <button
                onClick={() => (window.location.href = "/transactions")}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/view`}
              >
                <History className="h-4 w-4 mr-2 transition-transform group-hover/view:translate-x-0.5" />
                View All
              </button>
            </div>
            <div className="space-y-2">
              {recentDeposits.length > 0 ? (
                recentDeposits.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatFiat(transaction.amount)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(transaction.created_at).toLocaleDateString()}{" "}
                        • {transaction.tx_hash?.substring(0, 12)}...
                      </p>
                    </div>
                    <span
                      className={`${GlobalStyles.badge} ${
                        transaction.status === "confirmed"
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
                          : transaction.status === "pending"
                            ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                            : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800"
                      } px-2 py-0.5`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">
                    No deposits yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Info Panels */}
        <div className="space-y-4">
          {/* Important Info */}
          <div className={`${GlobalStyles.card}`}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Important Information
            </h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Minimum deposit: $10 USD equivalent
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Uses live prices
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  All deposits require confirmation
                </p>
              </div>
            </div>
          </div>

          {/* Current Balance */}
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Current Balance
              </h3>
              <WalletIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatFiat(user?.balance || 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Available for withdrawal
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deposit;
